import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import client, { auth } from '../api/client';

export default function RegisterPage({ onLogin }) {
  const [form, setForm] = useState({ username: '', email: '', password: '', password_confirm: '' });
  const [honeypot, setHoneypot] = useState('');
  const [loadedAt] = useState(Date.now());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (honeypot) { setError('Registration failed.'); return; }
    if (Date.now() - loadedAt < 3000) { setError('Please wait a moment before submitting.'); return; }
    setLoading(true);
    try {
      const res = await auth.register({ ...form, _ts: loadedAt });
      localStorage.setItem('token', res.data.token);
      onLogin(res.data.user);
      navigate('/dashboard');
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const messages = Object.entries(data).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`).join('\n');
        setError(messages);
      } else setError('Registration failed.');
    } finally { setLoading(false); }
  };

  const handleGoogleResponse = useCallback(async (response) => {
    try {
      setLoading(true); setError('');
      const res = await client.post('/auth/google/', { credential: response.credential });
      localStorage.setItem('token', res.data.token);
      onLogin(res.data.user);
      navigate('/dashboard');
    } catch { setError('Google signup failed.'); }
    finally { setLoading(false); }
  }, [onLogin, navigate]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({ client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '', callback: handleGoogleResponse });
        window.google.accounts.id.renderButton(document.getElementById('google-signup-btn'), { theme: 'outline', size: 'large', width: '100%', text: 'signup_with' });
      }
    };
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
  }, [handleGoogleResponse]);

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Tradrin</h1>
        <h2>Create an Account</h2>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleSubmit}>
          <input type="text" name="username" placeholder="Username" value={form.username} onChange={handleChange} required />
          <input type="email" name="email" placeholder="Email" value={form.email} onChange={handleChange} required />
          <input type="password" name="password" placeholder="Password" value={form.password} onChange={handleChange} required />
          <input type="password" name="password_confirm" placeholder="Confirm Password" value={form.password_confirm} onChange={handleChange} required />
          {/* Honeypot — hidden from humans, bots fill it */}
          <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)}
            autoComplete="off" tabIndex={-1} style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} />
          <button type="submit" disabled={loading}>{loading ? 'Creating...' : 'Create Account'}</button>
        </form>
        <div className="auth-divider"><span>or</span></div>
        <div id="google-signup-btn" className="google-btn-container"></div>
        <p>Already have an account? <Link to="/login">Sign In</Link></p>
      </div>
    </div>
  );
}
